import { createSelector } from "@reduxjs/toolkit";
import { WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Footer from "./Footer";
import Header from "./Header";
import Sidebar from "./VerticalLayout/Sidebar";

import { changeLayoutMode, changeLeftsidebarSizeType } from "../slices/thunk";

const Layout = ({ children }: any) => {
  const dispatch = useDispatch<any>();

  const selectLayoutState = (state: any) => state.Layout;
  const selectLayoutProperties = createSelector(selectLayoutState, (layout) => ({
    layoutModeType: layout.layoutModeType,
    layoutSidebarSizeType: layout.layoutSidebarSizeType,
    layoutSidebarColorType: layout.layoutSidebarColorType,
    layoutTopbarColorType: layout.layoutTopbarColorType,

    layoutType: "vertical",
    layoutSkintype: "default",
    layoutDirectionType: "ltr",
    layoutContentWidthType: "fluid",
    layoutNavigationType: "sticky",
  }));

  const {
    layoutModeType,
    layoutSidebarSizeType,
    layoutType,
    layoutSidebarColorType,
    layoutTopbarColorType,
  } = useSelector(selectLayoutProperties);

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  useEffect(() => {
    const applyFixedSettings = () => {
      document.documentElement.setAttribute("data-layout", "vertical");
      document.documentElement.setAttribute("data-skin", "default");
      document.documentElement.setAttribute("dir", "ltr");
      document.documentElement.setAttribute("data-content", "fluid");
      document.documentElement.setAttribute("data-navbar", "sticky");
    };

    applyFixedSettings();
    window.dispatchEvent(new Event("resize"));

    dispatch(changeLayoutMode(layoutModeType));
    dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType));
  }, [
    layoutModeType,
    layoutSidebarSizeType,
    layoutSidebarColorType,
    layoutTopbarColorType,
    dispatch,
  ]);

  useEffect(() => {
    document.documentElement.classList.add("scroll-smooth", "group");
    document.body.classList.add(
      "text-base",
      "bg-body-bg",
      "text-body",
      "font-public",
      "dark:text-zink-100",
      "dark:bg-zink-800",
      "group-data-[skin=bordered]:bg-body-bordered",
      "group-data-[skin=bordered]:dark:bg-zink-700"
    );
    return () => {
      document.documentElement.classList.remove("scroll-smooth", "group");
      document.body.classList.remove(
        "text-base",
        "bg-body-bg",
        "text-body",
        "font-public",
        "dark:text-zink-100",
        "dark:bg-zink-800",
        "group-data-[skin=bordered]:bg-body-bordered",
        "group-data-[skin=bordered]:dark:bg-zink-700"
      );
    };
  }, []);

  return (
    <>
      <div className="group-data-[sidebar-size=sm]:min-h-sm group-data-[sidebar-size=sm]:relative">
        <Sidebar layoutType={layoutType} layoutSidebarSizeType={layoutSidebarSizeType} />
        <Header />
        <div className="relative min-h-screen group-data-[sidebar-size=sm]:min-h-sm">
          <div className="group-data-[sidebar-size=lg]:ltr:md:ml-vertical-menu group-data-[sidebar-size=lg]:rtl:md:mr-vertical-menu group-data-[sidebar-size=md]:ltr:ml-vertical-menu-md group-data-[sidebar-size=md]:rtl:mr-vertical-menu-md group-data-[sidebar-size=sm]:ltr:ml-vertical-menu-sm group-data-[sidebar-size=sm]:rtl:mr-vertical-menu-sm pt-[calc(theme('spacing.header')_*_1)] pb-[calc(theme('spacing.header')_*_0.8)] px-4 group-data-[navbar=bordered]:pt-[calc(theme('spacing.header')_*_1.3)] group-data-[navbar=hidden]:pt-0 group-data-[layout=horizontal]:mx-auto group-data-[layout=horizontal]:max-w-screen-2xl group-data-[layout=horizontal]:px-0 group-data-[layout=horizontal]:group-data-[sidebar-size=lg]:ltr:md:ml-auto group-data-[layout=horizontal]:group-data-[sidebar-size=lg]:rtl:md:mr-auto group-data-[layout=horizontal]:md:pt-[calc(theme('spacing.header')_*_1.8)] group-data-[layout=horizontal]:px-3 group-data-[layout=horizontal]:group-data-[navbar=hidden]:pt-[calc(theme('spacing.header')_*_0.9)]">
            <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
              {isOffline && (
                <div className="flex items-center gap-2 px-4 py-2 mb-4 text-sm font-medium text-orange-800 bg-orange-100 border border-orange-200 rounded-md dark:bg-orange-500/20 dark:text-orange-300 dark:border-orange-500/30">
                  <WifiOff className="w-4 h-4 shrink-0" />
                  Sem conexão com a internet. A sincronização está pausada.
                </div>
              )}
              {children}
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default Layout;
